// client/src/components/pages/LexiStar/Messages/messageMeta.ts (converted from .js, ST-00206)
//
// Shared language for the driver channel UI (FR-NTF-008; LS-06): delivery
// state chips, channel labels, cost formatting, and the recipient line. Used
// by the message log, the conversation view, the driver profile, the rules
// pages and the notification bell.
//
// Delivery states are not asset/trip states, so they stay out of the LS-09 §4
// palette: neutral greys for in-flight, a green only for "read"/"delivered"
// confirmation, red for failed — always with a glyph and a word.
//
// ST-00206: labels for the driver acknowledgement reminders (DRIVER_ACK_REMINDER,
// DRIVER_ACK_FINAL_REMINDER, migration 034).

/** A lexistar.messages row as the delivery log / conversation views read it (loosely). */
export interface MessageLike {
  intent?: string | null;
  template_key?: string | null;
  driver_name?: string | null;
  recipient?: {
    kind?: string | null;
    ref?: string | number | null;
    address?: string | null;
    name?: string | null;
    driver_name?: string | null;
    user_name?: string | null;
  } | null;
  buttons?: unknown;
  cost?: unknown;
}

export interface StateMeta {
  label: string;
  glyph: string;
  className: string;
}

export const MESSAGE_STATES = [
  'queued',
  'sent',
  'delivered',
  'read',
  'failed',
  'suppressed',
  'received',
];

export const STATE_META: Record<string, StateMeta> = {
  queued: { label: 'Queued', glyph: '◷', className: 'bg-slate-100 text-slate-700' },
  sent: {
    label: 'Sent',
    glyph: '✓',
    className: 'bg-slate-100 text-slate-800 ring-1 ring-inset ring-slate-300',
  },
  delivered: { label: 'Delivered', glyph: '✓✓', className: 'bg-emerald-50 text-emerald-800' },
  read: { label: 'Read', glyph: '✓✓', className: 'bg-emerald-600 text-emerald-50' },
  failed: {
    label: 'Failed',
    glyph: '✖',
    className: 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-300',
  },
  suppressed: {
    label: 'Suppressed',
    glyph: '⊘',
    className: 'bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-300',
  },
  received: { label: 'Received', glyph: '↙', className: 'bg-sky-50 text-sky-800' },
};

export const stateMeta = (s: string | null | undefined): StateMeta =>
  (s ? STATE_META[s] : undefined) || {
    label: s || 'Unknown',
    glyph: '?',
    className: 'bg-slate-100 text-slate-600',
  };

export const CHANNELS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'telegram', label: 'Telegram' },
  { value: 'sms', label: 'SMS' },
  { value: 'in_app', label: 'In-app' },
  { value: 'fake', label: 'Test (fake)' },
];

export const channelLabel = (c: string | null | undefined): string =>
  CHANNELS.find((x) => x.value === c)?.label || c || '—';

const isNum = (v: unknown): boolean =>
  v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v));

/** R 0.35 — four-decimal ledger amounts shown to the cent (sub-cent as "< R0.01"). */
export function fmtZar(v: unknown): string {
  if (!isNum(v)) return '—';
  const n = Number(v);
  if (n === 0) return 'R0.00';
  if (Math.abs(n) < 0.005) return '< R0.01';
  return `R${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Cost of one message: its `cost` jsonb ({amount_zar, units, …}) or a bare number. */
export function messageCost(m: MessageLike | null | undefined): number | null {
  const c = m?.cost;
  if (c === null || c === undefined) return null;
  if (typeof c === 'number' || typeof c === 'string') return isNum(c) ? Number(c) : null;
  const amount = (c as { amount_zar?: unknown }).amount_zar;
  return isNum(amount) ? Number(amount) : null;
}

/** "T. Nkosi · +27821234567" / "role lexistar.trips.view" / the address. */
export function recipientText(m: MessageLike | null | undefined): string {
  const r = m?.recipient || {};
  const name = m?.driver_name || r.name || r.driver_name || r.user_name;
  const address = r.address || r.ref;
  if (name && address && String(address) !== String(name)) return `${name} · ${address}`;
  return name || (address ? String(address) : r.kind || '—');
}

export const INTENT_LABELS: Record<string, string> = {
  DRIVER_TRIP_ASSIGNMENT: 'Trip assignment',
  DRIVER_NEXT_LOAD: 'Next load',
  // ST-00206 (034): the driver has not accepted the trip
  DRIVER_ACK_REMINDER: 'Acceptance reminder',
  DRIVER_ACK_FINAL_REMINDER: 'Last acceptance reminder',
  DRIVER_DEVIATION_QUERY: 'Deviation query',
  DRIVER_DEVIATION_APPROVED: 'Deviation approved',
  DRIVER_DEVIATION_REJECTED: 'Deviation rejected',
  DRIVER_ADVISORY: 'Route advisory',
  DRIVER_OFFLOAD_CONFIRM: 'Offload confirmation',
  DRIVER_PROBLEM_MENU: 'Problem menu',
  DRIVER_PROBLEM_ACK: 'Problem acknowledged',
  DRIVER_HELP: 'Help',
  DRIVER_EMERGENCY_ACK: 'Emergency acknowledged',
  DRIVER_OPT_OUT_ACK: 'Opt-out confirmed',
  DRIVER_UNKNOWN_INPUT: 'Help menu',
  CONTROLLER_EVENT: 'Event notification',
  CONTROLLER_ESCALATION: 'Escalation',
};

export const intentLabel = (m: MessageLike | null | undefined): string => {
  const k = m?.intent || m?.template_key;
  return (
    (k ? INTENT_LABELS[k] : undefined) || (k ? String(k).replace(/_/g, ' ').toLowerCase() : '—')
  );
};

/** Buttons jsonb → [{id,label}] (tolerates bare strings). */
export const buttonsOf = (
  m: MessageLike | null | undefined
): Array<{ id: string; label: string }> =>
  (Array.isArray(m?.buttons) ? (m.buttons as unknown[]) : []).map((raw, i) => {
    if (typeof raw === 'string') return { id: String(i), label: raw };
    const b = raw as { id?: string; label?: string; title?: string };
    return { id: b.id ?? String(i), label: b.label ?? b.title ?? String(b.id) };
  });
